import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  /** @type {MediaQueryList | null} */
  #openOnDesktopMq = null;
  /** @type {(() => void) | null} */
  #openOnDesktopApply = null;
  /** @type {MediaQueryList | null} */
  #sidebarCartDesktopMq = null;
  /** @type {(() => void) | null} */
  #onSidebarCartDesktopMqChange = null;

  /** @returns {boolean} Permanent left nav on desktop is not a modal; do not trap focus there. */
  #shouldTrapFocus() {
    if (this.dataset.desktopFixedSidebar !== 'true') return true;
    return !window.matchMedia('(min-width: 750px)').matches;
  }

  #trapMenuDrawerFocus() {
    if (this.#shouldTrapFocus()) trapFocus(this.refs.menuDrawer);
  }

  /** @type {() => void} */
  #onDialogOpen = () => {
    if (this.dataset.desktopFixedSidebar === 'true' && window.matchMedia('(min-width: 750px)').matches) {
      removeTrapFocus();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    document.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);

    this.#syncDefaultOpenStateForViewport();
    this.#setupSidebarCartCountSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);

    if (this.#sidebarCartDesktopMq && this.#onSidebarCartDesktopMqChange) {
      if (typeof this.#sidebarCartDesktopMq.removeEventListener === 'function') {
        this.#sidebarCartDesktopMq.removeEventListener('change', this.#onSidebarCartDesktopMqChange);
      } else if (typeof this.#sidebarCartDesktopMq.removeListener === 'function') {
        this.#sidebarCartDesktopMq.removeListener(this.#onSidebarCartDesktopMqChange);
      }
    }
    this.#sidebarCartDesktopMq = null;
    this.#onSidebarCartDesktopMqChange = null;

    if (this.#openOnDesktopMq && this.#openOnDesktopApply) {
      if (typeof this.#openOnDesktopMq.removeEventListener === 'function') {
        this.#openOnDesktopMq.removeEventListener('change', this.#openOnDesktopApply);
      } else if (typeof this.#openOnDesktopMq.removeListener === 'function') {
        // Safari fallback
        this.#openOnDesktopMq.removeListener(this.#openOnDesktopApply);
      }
    }

    this.#openOnDesktopMq = null;
    this.#openOnDesktopApply = null;
  }

  /** Fixed left column (≥750px): keep “Cart (n)” in sync with `cart:update`. Hidden on mobile drawer / when not in desktop column. */
  #setupSidebarCartCountSync() {
    if (this.dataset.desktopFixedSidebar !== 'true') return;
    if (!this.querySelector('[data-header-drawer-cart-count]')) return;

    this.#sidebarCartDesktopMq = window.matchMedia('(min-width: 750px)');
    this.#onSidebarCartDesktopMqChange = () => {
      if (!this.#sidebarCartDesktopMq) return;
      if (this.#sidebarCartDesktopMq.matches) {
        const first = this.querySelector('[data-header-drawer-cart-count]');
        if (first instanceof HTMLElement) {
          const n = parseInt(first.dataset.cartItemCount ?? '0', 10);
          this.#setSidebarCartCountElements(n);
        }
      } else {
        this.#resetSidebarCartAriaForMobileDrawer();
      }
    };

    if (typeof this.#sidebarCartDesktopMq.addEventListener === 'function') {
      this.#sidebarCartDesktopMq.addEventListener('change', this.#onSidebarCartDesktopMqChange);
    } else {
      this.#sidebarCartDesktopMq.addListener(this.#onSidebarCartDesktopMqChange);
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#onSidebarCartDesktopMqChange();
  }

  #resetSidebarCartAriaForMobileDrawer() {
    for (const el of this.querySelectorAll('[data-header-drawer-cart-count]')) {
      const control = el.closest('a, button');
      const fallback = control instanceof HTMLElement ? control.dataset.cartAriaFallback : undefined;
      if (control instanceof HTMLElement && fallback) {
        control.setAttribute('aria-label', fallback);
      }
    }
  }

  #sidebarCartCountUpdatesActive() {
    return this.dataset.desktopFixedSidebar === 'true' && !!this.#sidebarCartDesktopMq?.matches;
  }

  /** @param {number} next */
  #setSidebarCartCountElements(next) {
    const counts = this.querySelectorAll('[data-header-drawer-cart-count]');
    for (const el of counts) {
      if (!(el instanceof HTMLElement)) continue;
      el.dataset.cartItemCount = String(next);
      el.textContent = ` (${next})`;
      const control = el.closest('a, button');
      if (!(control instanceof HTMLElement) || !control.dataset.cartAriaPrefix) continue;
      if (this.#sidebarCartDesktopMq?.matches) {
        control.setAttribute('aria-label', `${control.dataset.cartAriaPrefix} (${next})`);
      } else {
        const fb = control.dataset.cartAriaFallback;
        if (fb) control.setAttribute('aria-label', fb);
      }
    }
  }

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    if (!this.#sidebarCartCountUpdatesActive()) return;
    if (!this.querySelector('[data-header-drawer-cart-count]')) return;

    const detail = /** @type {import('@theme/events').CartUpdateEvent} */ (event).detail;
    if (detail?.data?.didError) return;

    const itemCount = detail?.data?.itemCount ?? 0;
    const fromProductForm = detail?.data?.source === 'product-form-component';

    const first = this.querySelector('[data-header-drawer-cart-count]');
    if (!(first instanceof HTMLElement)) return;

    const prev = parseInt(first.dataset.cartItemCount ?? '0', 10);
    const next = fromProductForm ? prev + itemCount : itemCount;
    this.#setSidebarCartCountElements(next);
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    if (!this.#sidebarCartCountUpdatesActive()) return;
    if (!this.querySelector('[data-header-drawer-cart-count]')) return;

    const raw = sessionStorage.getItem('cart-count');
    if (!raw) return;

    try {
      const { value, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp >= 10000) return;
      const n = parseInt(value, 10);
      if (Number.isNaN(n) || n < 0) return;
      this.#setSidebarCartCountElements(n);
    } catch {
      /* ignore */
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => this.#trapMenuDrawerFocus(), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          this.#trapMenuDrawerFocus();
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * If enabled via data-open-on-desktop, open the drawer by default only on desktop widths.
   * We avoid calling this.open() because that would trap focus on page load.
   */
  #syncDefaultOpenStateForViewport() {
    if (this.dataset.openOnDesktop !== 'true') return;

    const details = this.refs.details;
    const summary = details.querySelector('summary');
    this.#openOnDesktopMq = window.matchMedia('(min-width: 750px)');

    this.#openOnDesktopApply = () => {
      const mq = this.#openOnDesktopMq;
      if (!mq) return;

      if (mq.matches) {
        // On desktop we keep the drawer open but never lock page scroll.
        details.removeAttribute('scroll-lock');
        document.documentElement.removeAttribute('scroll-lock');

        details.setAttribute('open', '');
        details.classList.add('menu-open');
        summary?.setAttribute('aria-expanded', 'true');
      } else {
        // Restore normal mobile behavior.
        details.setAttribute('scroll-lock', '');
        reset(details);
      }
    };

    this.#openOnDesktopApply();

    if (typeof this.#openOnDesktopMq.addEventListener === 'function') {
      this.#openOnDesktopMq.addEventListener('change', this.#openOnDesktopApply);
    } else if (typeof this.#openOnDesktopMq.addListener === 'function') {
      // Safari fallback
      this.#openOnDesktopMq.addListener(this.#openOnDesktopApply);
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
